import dropDownIcon from "../../../../assets/drop-down.svg";
import StyledInput from "../StyledInput";
import styles from "./AutocompleteInput.module.css";
import classNames from "classnames";
import { useEffect, useId, useMemo, useRef, useState } from "react";
import {
  StyledInputLabelProps,
  StyledInputProps,
  StyledInputValidationProps,
} from "../StyledInput/StyledInput";
import { Describable } from "../../../Results/resultTypes";
import { filterDescribable } from "../../../Results/resultUtils";

const strings = {
  listOfQuickClickOptions: "Autocomplete list of Quick Click options",
};

type AutocompleteInputProps<T extends Describable> = Omit<
  StyledInputProps,
  "value"
> &
  StyledInputLabelProps &
  StyledInputValidationProps & {
    containerClassName?: string;
    listClassName?: string;
    value: string;
    onSelect: (o: T) => void;
    selected: T | null;
    getOptions: () => T[];
  };

export default function AutocompleteInput<T extends Describable>({
  containerClassName,
  listClassName,
  onSelect,
  value,
  getOptions,
  inputClassName,
  ...props
}: AutocompleteInputProps<T>) {
  const optionsId = useId();

  const [options, setOptions] = useState<T[]>([]);
  const [isOptionsOpen, setIsOptionsOpen] = useState(false);
  const [activeOptionIndex, setActiveOptionIndex] = useState(-1);

  const filteredOptions = useMemo(
    () => options.filter((o) => filterDescribable(o, value)),
    [options, value],
  );

  const divRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLOListElement>(null);
  const listItemRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const activeDescendantId =
    isOptionsOpen &&
    activeOptionIndex > 0 &&
    activeOptionIndex < filteredOptions.length - 1
      ? `${optionsId}-${filteredOptions[activeOptionIndex].description}`
      : undefined;

  useEffect(() => {
    setOptions(getOptions);
  }, [getOptions]);

  useEffect(() => {
    if (!isOptionsOpen) {
      setActiveOptionIndex(-1);
    }
  }, [isOptionsOpen]);

  useEffect(() => {
    if (isOptionsOpen === true) {
      const handlePrev = (e: KeyboardEvent) => {
        e.preventDefault();
        const nextIndex = activeOptionIndex - 1;
        if (
          nextIndex >= 0 &&
          nextIndex < listItemRefs.current.length &&
          listItemRefs.current[nextIndex] !== null
        ) {
          listItemRefs.current[nextIndex]?.focus();
          setActiveOptionIndex(nextIndex);
        } else {
          inputRef?.current?.focus();
        }
      };

      const handleNext = (e: KeyboardEvent) => {
        e.preventDefault();
        const nextIndex = activeOptionIndex + 1;
        if (
          nextIndex < listItemRefs.current.length &&
          listItemRefs.current[nextIndex] !== null
        ) {
          listItemRefs.current[nextIndex]?.focus();
          setActiveOptionIndex(nextIndex);
        }
      };

      const handleEscape = (e: KeyboardEvent) => {
        if (isOptionsOpen) {
          e.preventDefault();
          e.stopPropagation();
          setIsOptionsOpen(false);
        }
      };

      const handleKeydown = (e: KeyboardEvent) => {
        switch (e.key) {
          case "Down":
          case "ArrowDown":
            if (e.altKey) {
              e.preventDefault();
              setIsOptionsOpen(true);
            } else {
              handleNext(e);
            }
            return;
          case "Up":
          case "ArrowUp":
            handlePrev(e);
            return;
          case "Esc":
          case "Escape":
            handleEscape(e);
            return;
        }
      };

      const handleListItemKeydown = (
        listItem: HTMLButtonElement | null,
        e: KeyboardEvent,
      ) => {
        if (e.key === "Enter" || e.key === "Space") {
          e.stopPropagation();
          listItem?.click();
        }
      };

      const inputRefCurr = inputRef?.current;
      inputRefCurr?.addEventListener("keyup", handleKeydown);

      const divRefCurr = divRef?.current;
      divRefCurr?.addEventListener("keydown", handleKeydown);

      const listRefCurr = listItemRefs.current;
      listRefCurr.forEach((listItem) => {
        listItem?.addEventListener("keydown", (e) =>
          handleListItemKeydown(listItem, e),
        );
      });

      return () => {
        inputRefCurr?.removeEventListener("keydown", handleKeydown);
        divRefCurr?.removeEventListener("keydown", handleKeydown);
        listRefCurr.forEach((listItem) => {
          listItem?.removeEventListener("keydown", (e) =>
            handleListItemKeydown(listItem, e),
          );
        });
      };
    }
  }, [isOptionsOpen, activeOptionIndex]);

  return (
    <div
      ref={divRef}
      className={classNames(styles.container, containerClassName)}
    >
      <div className={styles.inputContainer}>
        <StyledInput
          {...props}
          ref={inputRef}
          inputClassName={classNames(styles.input, inputClassName)}
          onFocus={() => {
            setIsOptionsOpen(true);
            setActiveOptionIndex(-1);
          }}
          onBlur={(e) => {
            const index = listItemRefs.current.findIndex(
              (r) => r !== null && r === e.relatedTarget,
            );
            if (e.relatedTarget !== listRef.current && index < 0) {
              setIsOptionsOpen(false);
            }
          }}
          value={value}
          role="combobox"
          autoComplete="chrome-off"
          aria-autocomplete="list"
          aria-expanded={isOptionsOpen}
          aria-controls={optionsId}
          aria-activedescendant={activeDescendantId}
        />
        <img
          className={styles.dropdownArrow}
          src={dropDownIcon}
          alt=""
          aria-hidden={true}
        />
      </div>
      <ul
        ref={listRef}
        id={optionsId}
        role="listbox"
        aria-label={strings.listOfQuickClickOptions}
        className={classNames(styles.list, listClassName, {
          [styles.closed]: !isOptionsOpen,
        })}
        tabIndex={-1}
        onBlur={(e) => {
          const index = listItemRefs.current.findIndex(
            (r) => r !== null && r === e.relatedTarget,
          );
          if (e.relatedTarget !== listRef.current && index < 0) {
            setIsOptionsOpen(false);
          }
        }}
      >
        {filteredOptions.map((option, index) => {
          return (
            <li
              key={option.description}
              id={`${optionsId}-${option.description}`}
              className={styles.item}
              role="option"
              aria-selected={index === activeOptionIndex}
              tabIndex={-1}
            >
              <button
                ref={(element) => {
                  listItemRefs.current[index] = element;
                }}
                type="button"
                className={styles.button}
                onBlur={(e) => {
                  const i = listItemRefs.current.findIndex(
                    (r) => r !== null && r === e.relatedTarget,
                  );
                  if (
                    e.relatedTarget !== listRef.current &&
                    i < 0 &&
                    inputRef?.current !== e.relatedTarget
                  ) {
                    setIsOptionsOpen(false);
                  }
                }}
                onClick={() => {
                  onSelect(option);
                  setIsOptionsOpen(false);
                }}
                tabIndex={-1}
              >
                {option.description}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
